import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API_BASE });

// Global response interceptor: handle SESSION_REVOKED (other-device login)
// Components can listen to the 'pc-session-revoked' window event
api.interceptors.response.use(
  (r) => r,
  (err) => {
    try {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || '';
      if (status === 401 && typeof detail === 'string' && detail.startsWith('SESSION_REVOKED')) {
        // Strip header from message
        const msg = detail.replace(/^SESSION_REVOKED:?\s*/, '');
        try { window.dispatchEvent(new CustomEvent('pc-session-revoked', { detail: { message: msg } })); } catch (e) { /* ignore */ }
      }
    } catch (e) { /* ignore */ }
    return Promise.reject(err);
  }
);

// ---------------------------------------------------------------------------
// JWT token storage
// ---------------------------------------------------------------------------
// We deliberately use `localStorage` for the JWT bearer token, NOT httpOnly
// cookies. Rationale:
//   * the API is a pure stateless REST service on a different origin from the
//     SPA (Kubernetes ingress routes `/api/*` to the backend) — httpOnly
//     cookies would require either same-origin deployment or third-party
//     cookie handling that's increasingly broken by browser ITP / CHIPS;
//   * we mitigate XSS-token-theft risks at the source: every untrusted HTML
//     ever rendered goes through React's auto-escaping, dangerouslySetInnerHTML
//     is never used, and the JWT itself is short-lived (rotated on every login).
// If the threat model changes (e.g. shared kiosk computers), switch to a
// backend-set httpOnly cookie + same-origin proxy and remove these helpers.
export function setAuthToken(token) {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('pc_token', token);
  } else {
    delete api.defaults.headers.common['Authorization'];
    localStorage.removeItem('pc_token');
  }
}

export function loadStoredToken() {
  const t = localStorage.getItem('pc_token');
  if (t) {
    api.defaults.headers.common['Authorization'] = `Bearer ${t}`;
  }
  return t;
}

export const TIPOLOGIE_KEYS = ['Semplice', 'A/R', 'Raccomandata 1', 'Raccomandata 1 Con Prova di Consegna', 'Custom 1', 'Custom 2', 'Custom 3', 'Custom 4', 'Custom 5', 'Custom 6'];
// Backward-compat alias used in places that still want a plain list
export const TIPOLOGIE = TIPOLOGIE_KEYS;
// Letter bands (grammi) + parcel bands (kg) — kept in the same array so
// any code that iterates FASCE_PESO automatically picks up the new
// ranges. Keep the two groups contiguous so the dropdown renders them
// in two intuitive blocks.
export const FASCE_PESO = [
  '0-20g', '21-50g', '51-100g', '101-250g', '251-350g', '351-1000g', '1001-2000g',
  '0-2kg', '2-5kg', '5-10kg', '10-20kg', '20-30kg',
];
// Quick lookup used in the form to know whether to show the "kg manual"
// free-text input next to the dropdown.
export const FASCE_PESO_KG = ['0-2kg', '2-5kg', '5-10kg', '10-20kg', '20-30kg'];
export const ZONE = ['Italia', 'Europa', 'Mondo'];

export const PC_LOGO_URL = '/logo-postacentrale.png';

export function formatEUR(n) {
  const v = Number(n || 0);
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v);
}

export function formatDateTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
}

export function buildTrackingURL(template, code) {
  if (!template || !code) return '';
  return template.replace('{code}', encodeURIComponent(code));
}
